import React from 'react'
import { useParams, useLocation, useHistory } from 'react-router-dom'
import {
  getFormattedDateAndTime,
  useAuth,
  CompanyMemberRole,
  getResultLabelColor,
  GroupMemberRole,
  getFormattedAddress,
  useMemberDetails,
  useMemberTestSessions,
} from '@modmd/data'
import Label from 'components/Label'
import { QuestionnaireHistory } from 'sharedComponents/QuestionnaireHistory'
import { CompanyPageLayout } from 'client-portal/components/PageLayout'
import { DownloadTestResultPdfButton } from 'sharedComponents/DownloadTestResultPdfButton'
import Button from 'components/Button'
import { Details } from 'sharedComponents/Details'
import Datalist from 'components/Datalist'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE, COLOR, RADIUS } from 'theme'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import Icon from 'components/Icon'
import { Text } from 'components/Typography'
import { Tabber } from 'components/Tabber'
import { COMPANY_ROUTES, ROUTES } from 'client-portal/constants/routes'
import { VaccineCard } from 'sharedComponents/VaccineRecord'
import { Box } from 'components/Layout'

interface Params {
  memberId: string
  companyId: string
}

enum DatalistType {
  TEST_SESSIONS = 'testSessions',
  QUESTIONNAIRE_HISTORY = 'questionnaireHistory',
  VACCINE_RECORD = 'vaccine',
}

export const MemberDetails = () => {
  const { companyId, memberId } = useParams<Params>()
  const { data, getUserCompaniesWithRole, getUserGroupsWithRole } = useAuth()
  const { hasCompaniesWithRole } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const history = useHistory()
  const location = useLocation<{ from: string }>()
  const prevLocationState = location.state ? location.state.from : undefined
  const { hasGroupsWithRole: isCoordinator, groups: coordinatorGroups } = getUserGroupsWithRole({
    companyIds: [companyId],
    roles: [GroupMemberRole.Coordinator],
  })
  const { hasGroupsWithRole: isGroupManager, groups: managerGroups } = getUserGroupsWithRole({
    companyIds: [companyId],
    roles: [GroupMemberRole.Manager],
  })
  const [showedDatalist, setShowedDatalist] = React.useState<string>(DatalistType.TEST_SESSIONS)
  const [page, setPage] = React.useState(1)

  const isCoordinatorOnly = isCoordinator && !hasCompaniesWithRole

  const groupId = getUserGroupsWithRole({
    companyIds: [companyId],
    roles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator, GroupMemberRole.Member],
  })

  const { data: memberDetails, loading: isMemberDetailsLoading } = useMemberDetails({
    userId: memberId,
    groupId: groupId?.groups[0]?.id || '',
  })

  const user = React.useMemo(() => (data.User.id === memberId ? data.User : memberDetails?.user), [
    data.User,
    memberDetails?.user,
    memberId,
  ])

  const canSeeResult = React.useMemo(
    () =>
      memberDetails?.user?.GroupUsers?.some(
        (group) => groupId.groups.some((grp) => grp.id === group?.groupId) && group?.acceptedAt
      ),
    [memberDetails?.user, groupId]
  )

  const { data: testSessionsData, loading: isLoadingTestSessionsData } = useMemberTestSessions({
    userId: memberId,
    companyId,
    page,
    groupIds: hasCompaniesWithRole
      ? undefined
      : [...coordinatorGroups.map(({ id }) => id), ...managerGroups.map(({ id }) => id)],
    skipResult: isCoordinatorOnly,
  })

  const handleGoBack = React.useCallback(() => {
    if (prevLocationState) {
      history.goBack()
    } else {
      history.push(`/${ROUTES.COMPANY}/${companyId}/${COMPANY_ROUTES.TEST_SESSIONS}`)
    }
  }, [companyId, history, prevLocationState])

  const testSessionCount = testSessionsData?.testSessions.pagination.total ?? 0
  const pageLength = testSessionsData?.testSessions.pagination.length ?? 0
  const canSeeQuestionnaireHistory = hasCompaniesWithRole || isGroupManager

  const TestSession = () => (
    <>
      <Card borderRadius={`0 0 ${RADIUS.RADIUS_5} ${RADIUS.RADIUS_5}`}>
        <Datalist
          isLoading={isLoadingTestSessionsData}
          skeletonProps={{
            columns: 4,
          }}
          data={
            testSessionsData?.testSessions.testSessions?.map(({ id, date, Location, Tests }) => ({
              id,
              date: getFormattedDateAndTime(date),
              location: getFormattedAddress(Location),
              test: Tests.find(({ Members }) => Members.some(({ User }) => User.id === memberId)),
            })) ?? []
          }
          renderTableHeader={() => (
            <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
              <Datalist.HeaderCell label="id" />
              <Datalist.HeaderCell label="date" />
              <Datalist.HeaderCell label="location" />
              <Datalist.HeaderCell label="result" />
            </Datalist.Row>
          )}
          shouldRenderTableHeader={isDesktop}
          renderTableRow={({ id, date, location, test }) => (
            <Datalist.Row
              display="grid"
              gridTemplateColumns={{
                _: 'repeat(2, 1fr)',
                DESKTOP: 'repeat(4, 1fr)',
              }}
            >
              <Datalist.Cell label="id">{id}</Datalist.Cell>
              <Datalist.Cell label="date">{date}</Datalist.Cell>
              <Datalist.Cell label="location">{location}</Datalist.Cell>
              <Datalist.Cell label="result">
                {canSeeResult ? (
                  <>
                    <Label indicator={test?.result ? getResultLabelColor(test.result) : 'blue'}>
                      {test?.result ?? 'Pending result'}
                    </Label>
                    {!!test?.result && !!test?.id && (
                      <div>
                        <DownloadTestResultPdfButton
                          testId={test.id}
                          appearance="ghost"
                          size="small"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <Box display="grid">
                    <Text fontSize="xs" color={COLOR.grayDark}>
                      Pending user authorization
                    </Text>
                  </Box>
                )}
              </Datalist.Cell>
            </Datalist.Row>
          )}
        />
      </Card>
      {testSessionCount > pageLength && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(testSessionCount / pageLength)}
            currentPage={page}
            onPageChange={setPage}
          />
        </Pagination.Wrapper>
      )}
    </>
  )

  return (
    <CompanyPageLayout>
      <Details
        topContent={
          <Datalist
            isLoading={isMemberDetailsLoading}
            header={
              <Datalist.Title
                leftContent={
                  <>
                    <Button
                      size="small"
                      colorVariant="secondary"
                      appearance="link"
                      onClick={handleGoBack}
                      ml="auto"
                    >
                      <Icon.ChevronLeft size="XXL" />
                    </Button>
                    <Text fontSize="xl">
                      {memberDetails?.user &&
                        `${(memberDetails.user.firstName as string) || ''} ${
                          (memberDetails.user.lastName as string) || ''
                        }`}
                    </Text>
                  </>
                }
              />
            }
            data={[
              {
                email: user?.email,
                firstName: user?.firstName,
                lastName: user?.lastName,
              },
            ]}
            renderTableHeader={() => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(3, 1fr)">
                <Datalist.HeaderCell label="email" />
                <Datalist.HeaderCell label="first name" />
                <Datalist.HeaderCell label="last name" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader={isDesktop}
            renderTableRow={({ email, firstName, lastName }) => (
              <Datalist.Row
                display="grid"
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: 'repeat(3, 1fr)',
                }}
              >
                <Datalist.Cell label="email">{email}</Datalist.Cell>
                <Datalist.Cell label="first name">{firstName}</Datalist.Cell>
                <Datalist.Cell label="last name">{lastName}</Datalist.Cell>
              </Datalist.Row>
            )}
          />
        }
      >
        <>
          <Card.Title
            backgroundColor={COLOR.white}
            borderRadius={`${RADIUS.RADIUS_5} ${RADIUS.RADIUS_5} 0 0`}
            {...(canSeeQuestionnaireHistory && { p: '0' })}
          >
            {canSeeQuestionnaireHistory ? (
              <Tabber
                activeTabName={showedDatalist}
                onChange={({ activeTabName }) => {
                  setShowedDatalist(activeTabName)
                }}
              >
                <Tabber.Item tabName={DatalistType.TEST_SESSIONS} component={<TestSession />}>
                  Test Session
                </Tabber.Item>
                <Tabber.Item
                  tabName={DatalistType.QUESTIONNAIRE_HISTORY}
                  component={<QuestionnaireHistory userId={memberId} />}
                >
                  Questionnaire History
                </Tabber.Item>
                <Tabber.Item
                  tabName={DatalistType.VACCINE_RECORD}
                  component={<VaccineCard isEditable={false} userId={memberId} />}
                >
                  Vaccine record
                </Tabber.Item>
              </Tabber>
            ) : (
              <>Test Sessions</>
            )}
          </Card.Title>
        </>
      </Details>
    </CompanyPageLayout>
  )
}
